import React, { useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
    Button,
    CircularProgress,
    Drawer,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import useApiData from "../../services/useApiData.js";
import PostPrintLabel from "../../services/PostPrintLabel.js";
import PostData from "../../services/PostData.js";
import { openPdfInNewTab } from "../../services/openPrintTab.js";
import OpenLabelRecord from "./OpenLabelRecord.jsx";
import SnackbarEx from "../Helpers/SnackbarEx";
import GridFilterBar, { useGridFilter } from "../Helpers/GridFilterBar.jsx";
import GridLayoutControls from "../Helpers/GridLayoutControls.jsx";
import useGridLayout from "../Helpers/useGridLayout.js";
import { GRID_LAYOUT_KEYS } from "../Helpers/gridLayoutKeys.js";
import { NEEDS_LABEL_FILTER_FIELDS } from "../Helpers/gridFilterFields.js";
import {
    appDataGridDefaultProps,
    appDataGridSlotProps,
    emptySelectionModel,
    selectionIds,
} from "../Helpers/dataGridStyles.js";
import {
    catalogDataGridSx,
    catalogPrimaryButtonSx,
    catalogQuietButtonSx,
    catalogSearchFieldSx,
} from "../Products/catalogGridStyles.js";
import "../Products/catalog.css";

export default function PrintLabelGrid() {
    const listUrl = "api/broylesllc/catalog-items?needsLabel=true&pageSize=500";
    const { data, error, load: loading, refetch } = useApiData(listUrl);
    const records = data?.items || [];

    const [searchInput, setSearchInput] = useState("");
    const [selectionModel, setSelectionModel] = useState(emptySelectionModel);
    const [singleRecSel, setSingleRecSel] = useState(null);
    const [printing, setPrinting] = useState(false);
    const [openSnack, setOpenSnack] = useState({
        color: "",
        message: "",
        open: false,
    });
    const { apply: applyFilter, barProps: filterBar } = useGridFilter(NEEDS_LABEL_FILTER_FIELDS);

    const rows = useMemo(
        () =>
            records.map((item) => ({
                id: String(item._id),
                parserItemId: item.parserItemId || item.itemId || "",
                platform: item.platform || "",
                itemName: item.automatedInvoiceItemName || "",
                dateUploaded: item.invoiceAutomationUploadDate
                    ? new Date(item.invoiceAutomationUploadDate).toLocaleDateString()
                    : "",
                dateUploadedRaw: item.invoiceAutomationUploadDate || null,
            })),
        [records]
    );

    const visibleRows = useMemo(() => {
        const q = searchInput.trim().toLowerCase();
        const searched = !q
            ? rows
            : rows.filter((row) =>
                  [row.parserItemId, row.platform, row.itemName, row.dateUploaded]
                      .join(" ")
                      .toLowerCase()
                      .includes(q)
              );
        return applyFilter(searched);
    }, [rows, searchInput, applyFilter]);

    const selectedIds = selectionIds(selectionModel);
    const selectedRecords = rows
        .filter((row) => selectedIds.includes(row.id))
        .map((row) => ({ id: row.id, itemNum: row.parserItemId }));

    const layout = useGridLayout(GRID_LAYOUT_KEYS.printLabels, {
        sortModel: [],
        columnVisibilityModel: {},
    });

    const baseColumns = useMemo(
        () => [
            { field: "parserItemId", headerName: "Parser Item ID", minWidth: 180, flex: 0.8 },
            { field: "platform", headerName: "Platform", minWidth: 160, flex: 0.6 },
            { field: "itemName", headerName: "Item Name", minWidth: 280, flex: 1.4 },
            { field: "dateUploaded", headerName: "Uploaded", minWidth: 130, flex: 0.5 },
        ],
        []
    );

    const columns = useMemo(
        () => layout.applyWidths(baseColumns),
        [baseColumns, layout.committedWidths, layout.applyWidths]
    );

    const clearSelection = () => setSelectionModel(emptySelectionModel());

    const handlePrint = async () => {
        const selected = selectedRecords;
        if (!selected.length) return;
        if (selected.some((rec) => !String(rec.itemNum || "").trim())) {
            setOpenSnack({
                color: "warning",
                message: "A Parser Item ID is required to print a label.",
                open: true,
            });
            return;
        }

        setPrinting(true);
        try {
            const { data: pdf, error: printError } = await PostPrintLabel(
                selected,
                "broylesllc/print-labels"
            );
            if (pdf) {
                if (!openPdfInNewTab(pdf)) {
                    setOpenSnack({
                        color: "warning",
                        message: "Allow popups for this site to print labels.",
                        open: true,
                    });
                    return;
                }
                try {
                    const { success } = await PostData(
                        {
                            ids: selected.map((rec) => rec.id),
                            status: "Inspection",
                        },
                        "broylesllc/catalog-items/clear-needs-label",
                        null
                    );
                    if (success) {
                        clearSelection();
                        refetch();
                    } else {
                        setOpenSnack({
                            color: "warning",
                            message:
                                "Label printed, but the item is still marked as needing a label.",
                            open: true,
                        });
                    }
                } catch (err) {
                    console.error("Failed to mark printed:", err);
                }
            } else if (printError) {
                setOpenSnack({
                    color: "error",
                    message: printError?.message || "Error printing label.",
                    open: true,
                });
            }
        } finally {
            setPrinting(false);
        }
    };

    const closeDrawer = (result) => {
        if (result === "success") {
            clearSelection();
            refetch();
        }
        setSingleRecSel(null);
    };

    return (
        <>
            {error && (
                <Typography color="error" sx={{ mb: 1, fontSize: 13, px: 0.5 }}>
                    Failed to load items that need a label.
                </Typography>
            )}
            <div className="catalog-grid-shell">
                <div className="catalog-toolbar">
                    <TextField
                        size="small"
                        placeholder="Search item, platform, or name…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        sx={catalogSearchFieldSx}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlinedIcon sx={{ fontSize: 18, color: "#9aa3ad" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    {selectedIds.length > 0 ? (
                        <span className="catalog-selection-hint">
                            {selectedIds.length} selected
                        </span>
                    ) : (
                        <span className="catalog-selection-hint">
                            {visibleRows.length} item{visibleRows.length === 1 ? "" : "s"}
                        </span>
                    )}
                    <div className="catalog-toolbar-spacer" />
                    <GridLayoutControls layout={layout} columns={baseColumns} />
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={loading}
                        startIcon={<RefreshOutlinedIcon fontSize="small" />}
                        onClick={() => {
                            clearSelection();
                            refetch();
                        }}
                        sx={catalogQuietButtonSx}
                    >
                        Refresh
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        disabled={!selectedRecords.length || printing}
                        startIcon={
                            printing ? (
                                <CircularProgress size={14} color="inherit" />
                            ) : (
                                <LocalPrintshopOutlinedIcon fontSize="small" />
                            )
                        }
                        onClick={handlePrint}
                        sx={catalogPrimaryButtonSx}
                    >
                        Print
                    </Button>
                </div>
                <GridFilterBar {...filterBar} />
                <div className="catalog-grid-main">
                    <DataGrid
                        {...appDataGridDefaultProps}
                        loading={Boolean(loading || printing)}
                        rows={visibleRows}
                        columns={columns}
                        checkboxSelection
                        disableRowSelectionOnClick
                        rowSelectionModel={selectionModel}
                        onRowSelectionModelChange={setSelectionModel}
                        sortModel={layout.sortModel}
                        onSortModelChange={layout.onSortModelChange}
                        columnVisibilityModel={layout.columnVisibilityModel}
                        onColumnVisibilityModelChange={layout.onColumnVisibilityModelChange}
                        onColumnWidthChange={layout.onColumnWidthChange}
                        onRowClick={(params, event) => {
                            const target = event?.target;
                            if (target?.closest?.(".MuiCheckbox-root, .MuiDataGrid-cellCheckbox")) {
                                return;
                            }
                            setSingleRecSel(params);
                        }}
                        pageSizeOptions={[25, 50, 100]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 25 } },
                        }}
                        slotProps={appDataGridSlotProps}
                        sx={catalogDataGridSx}
                        localeText={{
                            noRowsLabel: searchInput.trim()
                                ? "No items match that search."
                                : "No items need a label.",
                        }}
                    />
                </div>
            </div>

            <Drawer
                anchor="right"
                open={!!singleRecSel}
                onClose={() => closeDrawer()}
                sx={{
                    "& .MuiDrawer-paper": {
                        height: "100%",
                        width: { xs: "100%", sm: "min(480px, 36vw)" },
                        minWidth: { sm: 380 },
                        borderRadius: "14px 0 0 14px",
                        overflow: "hidden",
                    },
                }}
            >
                {singleRecSel ? (
                    <OpenLabelRecord
                        key={singleRecSel.id || singleRecSel.row?.id}
                        recordId={singleRecSel}
                        closeDrawer={closeDrawer}
                    />
                ) : null}
            </Drawer>

            {openSnack.open && (
                <SnackbarEx
                    color={openSnack.color}
                    message={openSnack.message}
                    open={openSnack.open}
                    close={() => setOpenSnack({ color: "", message: "", open: false })}
                />
            )}
        </>
    );
}
